"use client";

import React, { useEffect, useState, useRef } from "react";
import useStoreKeys from "@/hooks/useStoreKeys";
import { ProductData } from "@/lib/5874/types";
import { getSearchSpringClient } from "@/lib/searchspring/client";
import ProductRecommendationCarousel, {
	ProductRecommendationCarouselProps,
} from "../ProductRecommendationCarousel/ProductRecommendationCarousel";
import { useSearchSpringCookieContext } from "@/lib/searchspring/providers/SearchSpringCookieProvider";

interface SearchSpringProductRecommendationCarouselProps
	extends Omit<ProductRecommendationCarouselProps, "products" | "isLoading"> {
	productSku?: string;
}

const SearchSpringProductRecommendationCarousel: React.FC<
	SearchSpringProductRecommendationCarouselProps
> = ({
	title,
	searchSpringId,
	useProductCards,
	productSku,
	placementProductId,
}) => {
	const { store, lang } = useStoreKeys();
	const [products, setProducts] = useState<Array<ProductData> | undefined>([]);
	const [isLoading, setIsLoading] = useState(false);
	const cookieState = useSearchSpringCookieContext();
	if (!searchSpringId) return <></>;

	const client = getSearchSpringClient(store, lang);

	const recommendArgs = {
		siteId: client.siteId,
		tag: searchSpringId,
		limits: 16,
		product: productSku,
		shopper: cookieState.ssShopperId,
		cart: [cookieState.ssCartProducts],
		lastViewed: [cookieState.ssViewedProducts],
	};

	const hasExecuted = useRef(false);

	useEffect(() => {
		if (hasExecuted.current) return;

		hasExecuted.current = true;
		setIsLoading(true);

		let isUnmounted = false;

		const runRecommendations = async () => {
			try {
				const prelim = await client.preliminaryRecommend(recommendArgs);
				if (!isUnmounted) {
					setProducts(prelim?.results);
				}

				const enriched = await client.enrichedRecommend(recommendArgs);
				if (!isUnmounted) {
					setProducts(enriched?.results);
				}
			} catch (e) {
				console.error(e);
			} finally {
				if (!isUnmounted) {
					setIsLoading(false);
				}
			}
		};

		runRecommendations();

		return () => {
			isUnmounted = true;
			setProducts(undefined);
			setIsLoading(false);
		};
	}, [searchSpringId]);

	if (products === undefined || products.length === 0) return null;

	return (
		<ProductRecommendationCarousel
			placementProductId={placementProductId}
			searchSpringId={searchSpringId}
			products={products}
			title={title}
			useProductCards={useProductCards}
			isLoading={isLoading}
		/>
	);
};

export default SearchSpringProductRecommendationCarousel;
